import axiosInstance from '../../../shared/api/axiosInstance';
import type { PipelineDashboard } from '../../../generated/api/models/pipelineDashboard';
import type {
  Pipeline,
  PipelineCreate,
  PipelineUpdate,
  PipelineDefaults,
  CustomerSegment,
  Stage,
  StageCreate,
  StageUpdate,
  StageCriterion,
  StageCriterionCreate,
  StageCriterionUpdate,
  CriterionCompletionStatus,
  CriterionCompletionUpdate,
  AdvanceDealRequest,
  Organization,
  OrganizationCreate,
  OrganizationUpdate,
  Contact,
  ContactCreate,
  ContactUpdate,
  Deal,
  DealCreate,
  DealUpdate,
  Activity,
  ActivityCreate,
  ActivityUpdate,
  PipelineSummaryResponse,
  ListOrganizationsParams,
  ListContactsParams,
  ListDealsParams,
  PaginatedResponse,
} from '../types';

const BASE_URL = '/api/v1/pipeline';

// Pipeline API
export const pipelineApi = {
  list: async (includeCriteria = false): Promise<Pipeline[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/pipelines`, {
      params: includeCriteria ? { include_criteria: true } : undefined,
    });
    return response.data.data;
  },

  get: async (id: string, includeCriteria = false): Promise<Pipeline> => {
    const response = await axiosInstance.get(`${BASE_URL}/pipelines/${id}`, {
      params: includeCriteria ? { include_criteria: true } : undefined,
    });
    return response.data.data;
  },

  getDefaults: async (
    segment?: CustomerSegment,
    language?: string,
  ): Promise<PipelineDefaults> => {
    const response = await axiosInstance.get(`${BASE_URL}/pipelines/defaults`, {
      params: {
        ...(segment ? { segment } : {}),
        ...(language ? { language } : {}),
      },
    });
    return response.data.data;
  },

  create: async (data: PipelineCreate): Promise<Pipeline> => {
    const response = await axiosInstance.post(`${BASE_URL}/pipelines`, data);
    return response.data.data;
  },

  update: async (id: string, data: PipelineUpdate): Promise<Pipeline> => {
    const response = await axiosInstance.put(`${BASE_URL}/pipelines/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/pipelines/${id}`);
  },

  getSummary: async (id: string): Promise<PipelineSummaryResponse> => {
    const response = await axiosInstance.get(`${BASE_URL}/pipelines/${id}/summary`);
    return response.data.data;
  },

  // Stage operations
  createStage: async (pipelineId: string, data: StageCreate): Promise<Stage> => {
    const response = await axiosInstance.post(`${BASE_URL}/pipelines/${pipelineId}/stages`, data);
    return response.data.data;
  },

  updateStage: async (stageId: string, data: StageUpdate): Promise<Stage> => {
    const response = await axiosInstance.put(`${BASE_URL}/stages/${stageId}`, data);
    return response.data.data;
  },

  deleteStage: async (pipelineId: string, stageId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/pipelines/${pipelineId}/stages/${stageId}`);
  },

  reorderStages: async (pipelineId: string, stageIds: string[]): Promise<Pipeline> => {
    const response = await axiosInstance.post(`${BASE_URL}/pipelines/${pipelineId}/stages/reorder`, { stage_ids: stageIds });
    return response.data.data;
  },

  // Stage criteria operations
  listCriteria: async (stageId: string): Promise<StageCriterion[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/stages/${stageId}/criteria`);
    return response.data.data;
  },

  createCriterion: async (stageId: string, data: StageCriterionCreate): Promise<StageCriterion> => {
    const response = await axiosInstance.post(`${BASE_URL}/stages/${stageId}/criteria`, data);
    return response.data.data;
  },

  updateCriterion: async (stageId: string, criterionId: string, data: StageCriterionUpdate): Promise<StageCriterion> => {
    const response = await axiosInstance.put(`${BASE_URL}/stages/${stageId}/criteria/${criterionId}`, data);
    return response.data.data;
  },

  deleteCriterion: async (stageId: string, criterionId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/stages/${stageId}/criteria/${criterionId}`);
  },

  reorderCriteria: async (stageId: string, criterionIds: string[]): Promise<StageCriterion[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/stages/${stageId}/criteria/reorder`, { criterion_ids: criterionIds });
    return response.data.data;
  },
};

// Organization API
export const organizationApi = {
  list: async (params?: ListOrganizationsParams): Promise<PaginatedResponse<Organization>> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations`, { params });
    return response.data;
  },

  get: async (id: string): Promise<Organization> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations/${id}`);
    return response.data.data;
  },

  create: async (data: OrganizationCreate): Promise<Organization> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations`, data);
    return response.data.data;
  },

  update: async (id: string, data: OrganizationUpdate): Promise<Organization> => {
    const response = await axiosInstance.put(`${BASE_URL}/organizations/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/organizations/${id}`);
  },

  claim: async (id: string): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/organizations/${id}/claim`);
  },

  archive: async (id: string): Promise<{ action: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations/${id}/archive`);
    return response.data.data;
  },

  unarchive: async (id: string): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/organizations/${id}/unarchive`);
  },
};

// Contact API
export const contactApi = {
  list: async (params?: ListContactsParams): Promise<PaginatedResponse<Contact>> => {
    const response = await axiosInstance.get(`${BASE_URL}/contacts`, { params });
    return response.data;
  },

  get: async (id: string): Promise<Contact> => {
    const response = await axiosInstance.get(`${BASE_URL}/contacts/${id}`);
    return response.data.data;
  },

  create: async (data: ContactCreate): Promise<Contact> => {
    const response = await axiosInstance.post(`${BASE_URL}/contacts`, data);
    return response.data.data;
  },

  update: async (id: string, data: ContactUpdate): Promise<Contact> => {
    const response = await axiosInstance.put(`${BASE_URL}/contacts/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/contacts/${id}`);
  },

  getActivities: async (id: string): Promise<Activity[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/contacts/${id}/activities`);
    return response.data.data;
  },

  claim: async (id: string): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/contacts/${id}/claim`);
  },

  archive: async (id: string): Promise<{ action: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/contacts/${id}/archive`);
    return response.data.data;
  },

  unarchive: async (id: string): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/contacts/${id}/unarchive`);
  },

  getDeals: async (id: string, includeArchived = false): Promise<Deal[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/contacts/${id}/deals`, {
      params: { include_archived: includeArchived },
    });
    return response.data.data;
  },
};

// Deal API
export const dealApi = {
  list: async (params?: ListDealsParams): Promise<PaginatedResponse<Deal>> => {
    const response = await axiosInstance.get(`${BASE_URL}/deals`, { params });
    return response.data;
  },

  get: async (id: string): Promise<Deal> => {
    const response = await axiosInstance.get(`${BASE_URL}/deals/${id}`, {
      params: { include_contacts: true, include_company: true },
    });
    return response.data.data;
  },

  create: async (data: DealCreate): Promise<Deal> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals`, data);
    return response.data.data;
  },

  update: async (id: string, data: DealUpdate): Promise<Deal> => {
    const response = await axiosInstance.put(`${BASE_URL}/deals/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/deals/${id}`);
  },

  moveToStage: async (dealId: string, stageId: string, extra?: { loss_reason?: string; competitor?: string; actual_close_date?: string }): Promise<Deal> => {
    const response = await axiosInstance.put(`${BASE_URL}/deals/${dealId}/stage`, { stage_id: stageId, ...extra });
    return response.data.data;
  },

  getCriteriaStatus: async (dealId: string, stageId?: string): Promise<CriterionCompletionStatus[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/deals/${dealId}/criteria-status`, {
      params: stageId ? { stage_id: stageId } : undefined,
    });
    return response.data.data;
  },

  updateCriteriaCompletions: async (dealId: string, completions: CriterionCompletionUpdate[]): Promise<CriterionCompletionStatus[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/${dealId}/criteria-completions`, { completions });
    return response.data.data;
  },

  advance: async (dealId: string, data?: AdvanceDealRequest): Promise<Deal> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/${dealId}/advance`, data || {});
    return response.data.data;
  },

  addContact: async (
    dealId: string,
    contactId: string,
    role?: string,
    isPrimary?: boolean
  ): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/deals/${dealId}/contacts`, {
      contact_id: contactId,
      role: role || 'involved',
      is_primary: isPrimary,
    });
  },

  updateContact: async (
    dealId: string,
    contactId: string,
    data: { role?: string; is_primary?: boolean }
  ): Promise<void> => {
    await axiosInstance.put(`${BASE_URL}/deals/${dealId}/contacts/${contactId}`, data);
  },

  removeContact: async (dealId: string, contactId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/deals/${dealId}/contacts/${contactId}`);
  },

  getActivities: async (id: string): Promise<Activity[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/deals/${id}/activities`);
    return response.data.data;
  },

  duplicate: async (id: string): Promise<Deal> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/${id}/duplicate`);
    return response.data.data;
  },

  archive: async (id: string): Promise<Deal> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/${id}/archive`);
    return response.data.data;
  },

  unarchive: async (id: string): Promise<Deal> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/${id}/unarchive`);
    return response.data.data;
  },

  bulkDelete: async (ids: string[]): Promise<{ deleted: number }> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/bulk-delete`, { ids });
    return response.data;
  },

  bulkRestore: async (ids: string[]): Promise<{ restored: number }> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/bulk-restore`, { ids });
    return response.data;
  },

  bulkArchive: async (ids: string[]): Promise<{ archived: number }> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/bulk-archive`, { ids });
    return response.data;
  },
};


// Deal Task Board API
export interface DealBoardInfo {
  board_id: string | null;
  workspace_id: string | null;
  has_board: boolean;
}

export interface DealBoardCreateResult {
  board_id: string;
  workspace_id: string | null;
  is_new: boolean;
}

export const dealBoardApi = {
  /**
   * Get the task board for a deal (if it exists).
   */
  get: async (dealId: string): Promise<DealBoardInfo> => {
    const response = await axiosInstance.get(`${BASE_URL}/deals/${dealId}/board`);
    return response.data.data;
  },

  /**
   * Create or get the task board for a deal.
   * Creates a new board if one doesn't exist, returns existing board otherwise.
   */
  create: async (dealId: string): Promise<DealBoardCreateResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/${dealId}/board`);
    return response.data.data;
  },
};

// Setup Status API (lightweight, used by sidebar)
export interface SalesSetupStatus {
  has_companies: boolean;
  has_contacts: boolean;
  has_deals: boolean;
}

export const setupStatusApi = {
  get: async (): Promise<SalesSetupStatus> => {
    const response = await axiosInstance.get(`${BASE_URL}/setup-status`);
    return response.data.data;
  },
};

// Dashboard API
export const dashboardApi = {
  get: async (): Promise<PipelineDashboard> => {
    const response = await axiosInstance.get(`${BASE_URL}/dashboard`);
    return response.data;
  },
};

// Activity API
export const activityApi = {
  create: async (data: ActivityCreate): Promise<Activity> => {
    const response = await axiosInstance.post(`${BASE_URL}/activities`, data);
    return response.data.data;
  },

  listByContact: async (contactId: string): Promise<Activity[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/contacts/${contactId}/activities`);
    return response.data.data;
  },

  listByDeal: async (dealId: string): Promise<Activity[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/deals/${dealId}/activities`);
    return response.data.data;
  },

  listByCompany: async (organizationId: string): Promise<Activity[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations/${organizationId}/activities`);
    return response.data.data;
  },

  update: async (id: string, data: ActivityUpdate): Promise<Activity> => {
    const response = await axiosInstance.put(`${BASE_URL}/activities/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/activities/${id}`);
  },
};

// Reports API
export interface WinLossReport {
  period_days: number;
  won: { count: number; total_value: number; avg_value: number; unconvertible_count: number };
  lost: { count: number; total_value: number; avg_value: number; unconvertible_count: number };
  win_rate: number;
  total_closed: number;
  total_deals?: number;
  open_deals?: number;
}

export interface PipelineVelocityStage {
  stage_id: string;
  name: string;
  position: number;
  stage_type: string;
  avg_days: number;
  deal_count: number;
}

export interface PipelineVelocityReport {
  pipeline_id: string;
  period_days: number;
  stages: PipelineVelocityStage[];
  avg_total_days: number;
}

export interface RottingDeal {
  id: string;
  name: string;
  amount: number;
  stage_id: string;
  stage_name: string;
  days_in_stage: number;
  rotting_days_limit: number;
  days_overdue: number;
  owner_id: string | null;
  organization_id: string | null;
  primary_contact_id: string | null;
}

export interface ConversionFunnelStage {
  stage_id: string;
  name: string;
  position: number;
  stage_type: string;
  deals_reached: number;
  conversion_rate: number;
}

export interface ConversionFunnelReport {
  pipeline_id: string | null;
  period_days: number;
  stages: ConversionFunnelStage[];
}

export interface ForecastMonth {
  month: string;
  deal_count: number;
  total_value: number;
  weighted_value: number;
  // Deals whose amount could not be stated in the base currency (ERP P13), so the
  // month's total/weighted values understate reality by this many deals.
  unconvertible_count: number;
}

export interface ForecastReport {
  pipeline_id: string | null;
  months_ahead: number;
  months: ForecastMonth[];
  totals: {
    deal_count: number;
    total_value: number;
    weighted_value: number;
    unconvertible_count: number;
  };
}

export interface RevenueTrendMonth {
  month: string;
  deal_count: number;
  total_value: number;
  unconvertible_count: number;
}

export interface RevenueTrendsReport {
  period_days: number;
  months: RevenueTrendMonth[];
  totals: {
    deal_count: number;
    total_value: number;
    unconvertible_count: number;
  };
}

export interface LeaderboardEntry {
  owner_id: string;
  name: string;
  avatar_url: string | null;
  deals_won: number;
  total_value: number;
  avg_value: number;
  // Won deals this rep closed in a currency that couldn't be stated in the base
  // currency (ERP P13) — excluded from total_value / avg_value.
  unconvertible_count: number;
}

export interface TeamPerformanceMember {
  user_id: string;
  name: string;
  avatar_url: string | null;
  is_current_user: boolean;
  open_deals_count: number;
  open_deals_value: number;
  // Open deals in a currency that couldn't be stated in the base currency (ERP
  // P13) — excluded from open_deals_value.
  open_deals_unconvertible_count: number;
  won_deals_count: number;
  won_deals_value: number;
  // Won deals in a currency that couldn't be stated in the base currency —
  // excluded from won_deals_value.
  won_deals_unconvertible_count: number;
  win_rate: number;
}

function addOwnerIds(params: Record<string, string | number>, ownerIds?: string[]) {
  if (ownerIds && ownerIds.length > 0) {
    params.owner_ids = ownerIds.join(',');
  }
}

export const reportsApi = {
  getWinLoss: async (days: number = 30, ownerIds?: string[]): Promise<WinLossReport> => {
    const params: Record<string, string | number> = { days };
    addOwnerIds(params, ownerIds);
    const response = await axiosInstance.get(`${BASE_URL}/reports/win-loss`, { params });
    return response.data.data;
  },

  getPipelineVelocity: async (pipelineId?: string, days: number = 90, ownerIds?: string[]): Promise<PipelineVelocityReport> => {
    const params: Record<string, string | number> = { days };
    if (pipelineId) params.pipeline_id = pipelineId;
    addOwnerIds(params, ownerIds);
    const response = await axiosInstance.get(`${BASE_URL}/reports/pipeline-velocity`, { params });
    return response.data.data;
  },

  getRottingDeals: async (pipelineId?: string, ownerIds?: string[]): Promise<RottingDeal[]> => {
    const params: Record<string, string> = {};
    if (pipelineId) params.pipeline_id = pipelineId;
    addOwnerIds(params as Record<string, string | number>, ownerIds);
    const response = await axiosInstance.get(`${BASE_URL}/deals/rotting`, { params });
    return response.data.data;
  },

  getConversionFunnel: async (pipelineId?: string, days: number = 90, ownerIds?: string[]): Promise<ConversionFunnelReport> => {
    const params: Record<string, string | number> = { days };
    if (pipelineId) params.pipeline_id = pipelineId;
    addOwnerIds(params, ownerIds);
    const response = await axiosInstance.get(`${BASE_URL}/reports/conversion-funnel`, { params });
    return response.data.data;
  },

  getForecast: async (pipelineId?: string, monthsAhead: number = 3, ownerIds?: string[]): Promise<ForecastReport> => {
    const params: Record<string, string | number> = { months_ahead: monthsAhead };
    if (pipelineId) params.pipeline_id = pipelineId;
    addOwnerIds(params, ownerIds);
    const response = await axiosInstance.get(`${BASE_URL}/reports/forecast`, { params });
    return response.data.data;
  },

  getRevenueTrends: async (pipelineId?: string, days: number = 365, ownerIds?: string[]): Promise<RevenueTrendsReport> => {
    const params: Record<string, string | number> = { days };
    if (pipelineId) params.pipeline_id = pipelineId;
    addOwnerIds(params, ownerIds);
    const response = await axiosInstance.get(`${BASE_URL}/reports/revenue-trends`, { params });
    return response.data.data;
  },

  getLeaderboard: async (pipelineId?: string, days: number = 90, ownerIds?: string[]): Promise<LeaderboardEntry[]> => {
    const params: Record<string, string | number> = { days };
    if (pipelineId) params.pipeline_id = pipelineId;
    addOwnerIds(params, ownerIds);
    const response = await axiosInstance.get(`${BASE_URL}/reports/leaderboard`, { params });
    return response.data.data;
  },

  getTeamPerformance: async (pipelineId?: string, days: number = 90): Promise<TeamPerformanceMember[]> => {
    const params: Record<string, string | number> = { days };
    if (pipelineId) params.pipeline_id = pipelineId;
    const response = await axiosInstance.get(`${BASE_URL}/reports/team-performance`, { params });
    return response.data.data;
  },
};

// Contact Role type
export interface ContactRole {
  name: string;
  is_suggested: boolean;
}

// Sales Tenant Settings type
export interface SalesTenantSettings {
  id: string | null;
  tenant_id: string;
  custom_contact_roles: string[];
  settings: Record<string, unknown>;
  updated_by: string | null;
  prospecting_daily_send_limit?: number;
  prospecting_send_window_start?: string;
  prospecting_send_window_end?: string;
  prospecting_skip_weekends?: boolean;
}

// Settings API
export const salesSettingsApi = {
  getTenantSettings: async (): Promise<SalesTenantSettings> => {
    const response = await axiosInstance.get(`${BASE_URL}/settings/tenant`);
    return response.data;
  },

  updateTenantSettings: async (data: Partial<SalesTenantSettings>): Promise<SalesTenantSettings> => {
    const response = await axiosInstance.put(`${BASE_URL}/settings/tenant`, data);
    return response.data;
  },

  getContactRoles: async (): Promise<ContactRole[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/settings/contact-roles`);
    return response.data.data;
  },

  addCustomRole: async (role: string): Promise<string[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/settings/contact-roles`, { role });
    return response.data.data;
  },

  deleteCustomRole: async (role: string): Promise<string[]> => {
    const response = await axiosInstance.delete(`${BASE_URL}/settings/contact-roles/${encodeURIComponent(role)}`);
    return response.data.data;
  },
};
